<template>
  <div>
    <CCardBody>
      <div class="text-right btngroup">
        <CButton v-if="$can('create-role')" class="addBtn" @click="$bvModal.show('rolesModal')">
          Create
        </CButton>
      </div>
      <search v-model="search" float='right' :clickHandler="searchRole" placeholder="Type to search"/>
      <b-table :items="roles" :fields="headers" hover sorter show-empty empty-text="No data exists">
        <template #empty="scope">
          <h5>{{ scope.emptyText }}</h5>
        </template>
        <!-- AB#19866 (Phase 2): single-select company default — radio so picking one clears the rest. -->
        <template #cell(is_default)="item">
          <b-form-radio
            name="company-default-role"
            :value="item.item.id"
            :checked="item.item.is_default ? item.item.id : null"
            @change="setDefaultRole(item.item)">
          </b-form-radio>
        </template>
        <template #cell(actions)="item">
          <!-- AB#19866 (Phase 2): merchants may only edit/delete their own company roles, not superadmin-granted ones. -->
          <delete v-if="$can('delete-role') && canManage(item.item)" class="delete_role ml-2" @click="deleteRole(item.item.id)" fillColor="#E30000"
                  :id="item.item.id"/>
          <Pencil class="edit_role" v-if="$can('edit-role') && canManage(item.item)" @click="editRole(item)" fillColor="#009FE3"
                  :id="item.item.id"/>
          <!-- AB#19866 (Phase 2): merchants can assign permissions on their own roles AND on
               granted roles (editing a granted role materializes a per-company copy on save). -->
          <router-link v-if="$can('update-permissions')"
                       :to="{ name: 'assignpermissions', params: { id: item.item.id }}">
            <Pencil class="edit_role_permission" fillColor="#009FE3" :id="item.id"/>
          </router-link>
          <!-- AB#19866 (Phase 2): revert a customized granted role to the original. Hidden until enabled. -->
          <b-button v-if="enableRoleReset && isCopy(item.item)" size="sm" variant="link" class="p-0 ml-2 reset_role"
                    @click="resetRole(item.item)">Reset</b-button>
        </template>
      </b-table>
      <b-pagination v-model="pagination.currentPage" :total-rows="pagination.rows" :per-page="pagination.perPage" aria-controls="itemList"
          align="center" @change="handlePagination" v-if="pagination.total > 0" :key="pagination.currentPage">
      </b-pagination>
      <b-modal id="rolesModal" size="md" no-close-on-backdrop hide-footer @hidden="role_obj = getRoleObject()">
        <template #modal-title>
          <span>{{role_obj.id ? 'Update' : 'Create'}} Role</span>
        </template>
        <CRow>
          <CCol class="col-md-12">
            <CInput type="text" name="label" v-model="role_obj.label" v-validate="'required|alpha_spaces'"
                    placeholder="Enter role label">
            </CInput>
            <div v-for="error in errors.collect('label')" :key="error" class="error_msg">
              {{ error }}
            </div>

            <CInput type="text" class="mt-3 w-100 float-right" name="name" v-model="role_obj.name" v-validate="'required|alpha_dash'"
                    placeholder="Enter role name">
            </CInput>
            <div v-for="error in errors.collect('name')" :key="error" class="error_msg">
              {{ error }}
            </div>

            <!-- AB#19866 (Phase 2): merchant roles are always customizer (frontend) — no type picker. -->
            <b-form-select v-if="!isMerchant" class="mt-3" name="app_type" v-validate="{required: true}" v-model="role_obj.app_type"
                           :options="appTypeOptions">
            </b-form-select>
            <div v-if="!isMerchant && errors && errors.first('app_type')" class="error_msg">
              Type field is required
            </div>

            <CButton color="success" class="mt-5 w-100 float-right" @click="updateCreateRole">
              {{role_obj.id ? 'Update' : 'Save'}}
            </CButton>
          </CCol>
        </CRow>
      </b-modal>
    </CCardBody>
  </div>
</template>
<script>
import Pencil from 'vue-material-design-icons/GreasePencil.vue';
import Delete from 'vue-material-design-icons/Delete.vue';
import myMixins from '@/mixins/myMixins';
import { setPageModal, getLastPage } from "@/helpers/Helpers";
import {http} from "@/config/httpCommon"

export default {
  name: 'roles',
  mixins: [myMixins],
  components: {
    Delete,
    Pencil
  },
  data() {
    return {
      isUpdating: false,
      roleModal: false,
      role_obj: {},
      label: '',
      app_type: '',
      id: '',
      roles: [],
      modes: [
        {shape: 'pill'},
      ],
      colors: ['primary'],
      appTypeOptions: [
        {text: 'Please select a type', value: ''},
        {text: 'Admin', value: 'admin'},
        {text: 'Frontend', value: 'customizer'}
      ],
      pagination: {
        rows: null,
        currentPage: null,
        perPage: null,
        total: 0
      },
      search: '',
      // AB#19866 (Phase 2): "Reset" (revert a customized granted role to the original) is built
      // but hidden until the team decides to enable it. Flip to true to switch it on.
      enableRoleReset: false
    }
  },
  computed: {
    // AB#19866 (Phase 2): Company Admin / Merchant Group Admin are merchants.
    isMerchant() {
      const name = (this.$store.getters.userRole && this.$store.getters.userRole.name || '').toLowerCase();
      return name === 'admin' || name === 'merchantgroupadmin';
    },
    headers() {
      const cols = [
        {key: 'label', label: 'Label', sortable: true,},
        {key: 'app_type', label: 'Type',},
      ];
      // Merchants get a single-select "Default" column for the company default role.
      if (this.isMerchant) cols.push({key: 'is_default', label: 'Default',});
      cols.push({key: 'actions', label: 'Actions',});
      return cols;
    }
  },
  async created() {
    this.preservePaginationIgnoreSearch('page', 'search', this.$route.name, this.getRoles)
    this.role_obj = this.getRoleObject()
    await this.getRoles();
  },
  methods: {
    getRoleObject(update_props) {
      return {...{id: null, name: null, app_type: '', role:null}, ...update_props}
    },
    async getRoles(query_params = '') {
      if(query_params) {
        query_params += '&paginate=true'
      } else {
        query_params = '?paginate=true'
      }
      http.get(`role` + query_params).then((successResponse) => {
        const response_data = successResponse.data
        if (response_data.success) {
          this.roles = response_data.result.roles.data
          this.makePagination(response_data.result.roles)
        } else {
          this.showServerErrors(successResponse);
        }
      }).catch((e) => {
        this.handleResponseErrors(e)
      });
    },
    makePagination(data) {
      this.pagination.currentPage = data.current_page;
      this.pagination.rows = data.total;
      this.pagination.perPage = data.per_page;
      this.pagination.total = data.total;
    },
    searchRole(search) {
      let params = ''
      if (search) {
        params = '?search=' + search;
      }
      setPageModal(1,this.$route.name,this.search);
      this.getRoles(params);
    },
    showModal(type) {
      if (type == 'create') {
        this.label = '';
        this.app_type = '';
        this.isUpdating = false;
        this.id = '';
      }
      this.$bvModal.show("rolesModal");
    },
    async updateCreateRole() {
      // AB#19866 (Phase 2): merchants only create customizer (frontend) roles.
      if (this.isMerchant) {
        this.role_obj.app_type = 'customizer';
      }
      let valid = await this.$validator.validate();
      if (valid) {
        let action = 'post'
        let url = 'role'
        if(this.role_obj.id) {
          action = 'put'
          url = `role/${this.role_obj.id}`
        }
        http[action](url, this.role_obj).then(successResponse => {
          let response_data = successResponse.data
          if(response_data.success) {
            if(this.role_obj.id) {
              this.preservePagination( 'page', 'search', this.$route.name, this.getRoles)
            } else {
              this.nextPageOnCreation('page', 'search', this.roles.length, this.pagination.perPage, this.pagination.currentPage, getLastPage(this.pagination.perPage, this.pagination.total), this.$route.name, this.getRoles);
            }
            this.$bvModal.hide("rolesModal");
            this.showToast(response_data.message, 'SUCCESS')
          } else {
            this.showServerErrors(successResponse);
          }
        }).catch(errorResponse => {
          this.handleResponseErrors(errorResponse);
        })
      }
    },
    async deleteRole(role_id) {
      try {
        await this.doDeleteRole(role_id, false);
      } catch (errorResponse) {
        // AB#19866 (Phase 2, rule 4): backend asks to confirm when the role is assigned to merchants.
        const res = errorResponse && errorResponse.response;
        if (res && res.status === 409 && res.data && res.data.requires_confirmation) {
          const ok = await this.$bvModal.msgBoxConfirm(res.data.message, {
            title: 'Confirm deletion',
            okTitle: 'Delete anyway',
            okVariant: 'danger',
            cancelTitle: 'Cancel'
          });
          if (ok) {
            try {
              await this.doDeleteRole(role_id, true);
            } catch (err) {
              this.handleResponseErrors(err);
            }
          }
        } else {
          this.handleResponseErrors(errorResponse);
        }
      }
    },
    doDeleteRole(role_id, force) {
      const url = force ? `role/${role_id}?force=1` : `role/${role_id}`;
      return http.delete(url).then(successResponse => {
        const response_data = successResponse.data;
        if (response_data.success) {
          this.previousPageOnDeletion('page', 'search', this.roles.length - 1, this.$route.name, this.getRoles);
        } else {
          this.showServerErrors(successResponse);
        }
      });
    },
    editRole(item_data) {
      let role = item_data.item;
      this.role_obj = {
        id: role.id, name: role.name, app_type: role.app_type
      }
      this.$bvModal.show('rolesModal')
    },
    // AB#19866 (Phase 2): merchants can edit/delete only roles they created from scratch.
    // Superadmin-granted roles and per-company copies of them (is_manageable=false) are
    // permission-editable but not renamable/deletable.
    canManage(role) {
      return !this.isMerchant || role.is_manageable;
    },
    // AB#19866 (Phase 2): a copy = merchant-owned role materialized from a granted role.
    isCopy(role) {
      return this.isMerchant && role.is_owned && !!role.source_role_id;
    },
    // AB#19866 (Phase 2): revert a customized granted role — delete the copy, restore the original.
    resetRole(role) {
      http.post(`company/roles/${role.id}/reset`).then(successResponse => {
        const response_data = successResponse.data;
        if (response_data.success) {
          this.showToast(response_data.message, 'SUCCESS');
          this.getRoles(this.search ? ('?search=' + this.search) : '');
        } else {
          this.showServerErrors(successResponse);
        }
      }).catch(errorResponse => {
        this.handleResponseErrors(errorResponse);
      });
    },
    // AB#19866 (Phase 2): set this role as the company default (single-select; re-syncs existing customers).
    setDefaultRole(role) {
      if (role.is_default) return; // already the default — ignore re-clicks
      http.post('company/default-customizer-role', {role_id: role.id}).then(successResponse => {
        const response_data = successResponse.data;
        if (response_data.success) {
          this.showToast(response_data.message, 'SUCCESS');
          this.getRoles(this.search ? ('?search=' + this.search) : '');
        } else {
          this.showServerErrors(successResponse);
          this.getRoles();
        }
      }).catch(errorResponse => {
        this.handleResponseErrors(errorResponse);
        this.getRoles(); // restore checkbox state on failure
      });
    },
    async handlePagination(page = '') {
      let params = '?page=' + page;
      if (this.search) {
        params += '&search=' + this.search
      }
      setPageModal(page,this.$route.name,this.search);
      this.getRoles(params);
    }
  },

}
</script>
<style type="scss">
.delete_role {
  cursor: pointer;
}

.edit_role {
  cursor: pointer;
}
</style>